import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthState } from '../types';
const API_URL = process.env.REACT_APP_API_URL;

interface AuthContextType extends AuthState {
  login: (username: string, password: string, role: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

const storedToken = localStorage.getItem('token');
const storedUser = localStorage.getItem('user');

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken,
  isAuthenticated: !!(storedToken && storedUser),
  isLoading: !!(storedToken && storedUser) // Set loading true if we have stored auth data to validate
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false
      };
    case 'LOGIN_FAILURE':
      return { user: null, token: null, isAuthenticated: false, isLoading: false };
    case 'LOGOUT':
      return { user: null, token: null, isAuthenticated: false, isLoading: false };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token && user) {
      // Validate token and get user info
      validateToken(token);
    } else if (token || user) {
      // Clear inconsistent auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGIN_FAILURE' });
    } else {
      // No stored auth data, stop loading
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const validateToken = async (token: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      // Validate token by getting user info
      const response = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        let user = await response.json();

        // Fetch doctor-specific data if role is doctor
        if (user.role === 'doctor' && user.id) {
          try {
            const doctorResponse = await fetch(`${API_URL}/auth/users/${user.id}/doctor-info`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (doctorResponse.ok) {
              const doctorInfo = await doctorResponse.json();
              // Merge doctor info with base user data
              user = {
                ...user,
                ...doctorInfo
              };
            }
          } catch (error) {
            console.error('Failed to fetch doctor info:', error);
            // Continue with base user data if doctor-info fetch fails
          }
        }

        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        dispatch({ type: 'LOGIN_FAILURE' });
      }
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGIN_FAILURE' });
    }
  };

  const login = async (username: string, password: string, role: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });

      // Use JSON API as specified
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password,
          role
        })
      });

      if (response.ok) {
        const data = await response.json();
        const token = data.access_token;

        // Get user info after login
        const userResponse = await fetch(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (userResponse.ok) {
          let user = await userResponse.json();

          // Fetch doctor-specific data if role is doctor
          if (user.role === 'doctor' && user.id) {
            try {
              const doctorResponse = await fetch(`${API_URL}/auth/users/${user.id}/doctor-info`, {
                headers: { Authorization: `Bearer ${token}` }
              });

              if (doctorResponse.ok) {
                const doctorInfo = await doctorResponse.json();
                // Merge doctor info with base user data
                user = {
                  ...user,
                  ...doctorInfo
                };
              }
            } catch (error) {
              console.error('Failed to fetch doctor info:', error);
              // Continue with base user data if doctor-info fetch fails
            }
          }

          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
        } else {
          throw new Error('Failed to get user info');
        }
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        throw new Error('Login failed');
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch({ type: 'LOGOUT' }); // Use dispatch instead of setUser
    window.location.href = '/login';
  };

  const updateUser = (user: User) => {
    console.log('🔄 AuthContext updateUser called with:', user);
    dispatch({ type: 'UPDATE_USER', payload: user });
    localStorage.setItem('user', JSON.stringify(user));
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Export the useAuth hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};